import { _isNotEmpty } from '@/littledash';
import type { SendResultReport, SendVerdictTally } from '@/model/Agent.model';
import type { FC } from 'react';
import SendPartialCheck from './SendPartialCheck';
import { partialRulesOf } from './sendRuleOutcomes';
import styles from './sendResults.module.scss';

interface SendRuleChecksProps {
  report: SendResultReport;
  tally: SendVerdictTally;
  testId?: string;
}

const buckets: Array<{ key: keyof SendVerdictTally; label: string }> = [
  { key: 'passed', label: 'Passed' },
  { key: 'failed', label: 'Failed' },
  { key: 'not_applicable', label: 'Not applicable' },
  { key: 'not_evaluated', label: 'Not evaluated' },
];

/**
 * How the upload fared against the published SEND rules: the server's tally, as given, with how many
 * of its passes were not an exact, finished check, and the rules it was checked against only in part,
 * each with why. A deterministic account, so it carries no
 * Benchling In Vivo AI label.
 */
const SendRuleChecks: FC<SendRuleChecksProps> = ({ report, tally, testId = 'invivo-send-validator__rule-checks' }) => {
  const partial = partialRulesOf(report);
  return (
    <div className="mt3" data-test-component="SendRuleChecks" data-testid={testId}>
      <h3 className={`${styles.sectionTitle} mb1`}>Rule checks</h3>
      <p className="f6 dark-gray ma0 mb2" data-test-element="rule-checks-scope">
        {tally.total} published SEND conformance rules
      </p>
      <ul className={styles.tally} data-test-element="rule-checks-tally">
        {buckets.map(({ key, label }) => (
          <li key={key} className={styles.tallyTile} data-test-element="rule-checks-bucket" data-test-key={key}>
            <span className={styles.tallyValue}>{tally[key]}</span>
            <span className={styles.tallyLabel}>{label}</span>
            {key === 'passed' && tally.passed > tally.conforming && (
              <span className={`${styles.tallyLabel} ml1`} data-test-element="rule-checks-passed-in-part">
                ({tally.passed - tally.conforming} checked only in part)
              </span>
            )}
          </li>
        ))}
      </ul>
      {_isNotEmpty(partial) && (
        <div className="mt3" data-test-element="rule-checks-partial">
          <p className="f6 fw5 ma0 mb2">
            {partial.length} {partial.length === 1 ? 'rule was' : 'rules were'} checked only partially
          </p>
          <ul className="list pa0 ma0">
            {partial.map((rule) => (
              <li
                key={rule.ruleId}
                className="flex items-center mb2"
                data-test-element="rule-checks-partial-rule"
                data-test-key={rule.ruleId}
              >
                <span className="f6 fw6 near-black mr2">{rule.ruleId}</span>
                <SendPartialCheck className="mr2" />
                <span className="f6 dark-gray">{rule.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SendRuleChecks;
